import ApiService from "service/Api";
import { CatalogDescriptionPayloadTypes } from "utilities/types/CatalogDescription";
import {
  ReleaseVoucherPayloadTypes,
  VoucherPayloadTypes,
} from "utilities/types/Voucher";

const VoucherService = {
  getAllVoucherCategories: async () => {
    const endpoints = ["admin/catalog-categories", "admin/catalogs/categories"];

    for (const endpoint of endpoints) {
      try {
        const requestData = {
          method: "get",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
          url: endpoint,
        };

        const res = await ApiService.apiRequest(requestData);
        return res.data.data;
      } catch (error) {
        continue;
      }
    }

    throw new Error("Voucher categories endpoint is not available");
  },

  storeVoucherCategory: async (payload: {
    categoryName: string;
    isAllUser?: boolean;
    tenantId?: string | number | null;
  }) => {
    const requestData = {
      method: "post",
      headers: {
        "Content-Type": "application/json; multipart/form-data; charset=utf-8",
      },
      data: {
        name: payload.categoryName,
        is_all_user: Boolean(payload.isAllUser),
        tenant_id: payload.tenantId ?? null,
      },
      url: "admin/catalog-categories",
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data.data;
    } catch (error) {
      throw error;
    }
  },

  updateVoucherCategory: async (
    categoryId: string,
    payload: {
      categoryName: string;
      isAllUser?: boolean;
      tenantId?: string | number | null;
    }
  ) => {
    const requestData = {
      method: "post",
      headers: {
        "Content-Type": "application/json; multipart/form-data; charset=utf-8",
      },
      data: {
        name: payload.categoryName,
        is_all_user: Boolean(payload.isAllUser),
        tenant_id: payload.tenantId ?? null,
        _method: "PUT",
      },
      url: `admin/catalog-categories/${categoryId}`,
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data.data;
    } catch (error) {
      throw error;
    }
  },

  deleteVoucherCategory: async (categoryId: string) => {
    const requestData = {
      method: "delete",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      url: `admin/catalog-categories/${categoryId}`,
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      return res.data.data;
    } catch (error) {
      throw error;
    }
  },

  getAllVouchers: async (
    page: number,
    search: string,
    limit: number,
    filterTenant: number
  ) => {
    const requestData = {
      method: "get",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      params: {
        is_voucher: 1,
        search: search,
        page: page,
        per_page: limit,
        filter_tenant: filterTenant,
        portal: "admin",
      },
      url: "admin/catalogs",
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      const data = res.data.data;

      return data;
    } catch (error) {
      throw error;
    }
  },

  storeVoucher: async (payload: VoucherPayloadTypes) => {
    const {
      voucherName,
      pointsNeeded,
      voucherImage,
      voucherImageDetail,
      expiryDate,
      voucherType,
      merchant,
      pointTypeUsed,
      tenantId,
      isActive,
      maxClaim,
      categoryId,
    } = payload;

    const requestData = {
      method: "post",
      headers: {
        "Content-Type": "application/json; multipart/form-data; charset=utf-8",
      },
      data: {
        catalog_name: voucherName,
        points_needed: pointsNeeded,
        image: voucherImage,
        image_detail: voucherImageDetail,
        expiry_date: expiryDate,
        catalog_type: voucherType,
        is_voucher: 1,
        catalog_product: merchant,
        point_type_used: pointTypeUsed,
        tenant_id: tenantId,
        category_id: categoryId,
        is_active: isActive,
        max_claim: maxClaim,
      },
      url: "admin/catalogs",
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      const data = res.data.data;

      return data;
    } catch (error) {
      throw error;
    }
  },

  updateVoucher: async (voucherId: string, payload: VoucherPayloadTypes) => {
    const {
      voucherName,
      pointsNeeded,
      voucherImage,
      voucherImageDetail,
      expiryDate,
      voucherType,
      merchant,
      pointTypeUsed,
      tenantId,
      isActive,
      maxClaim,
      categoryId,
    } = payload;

    const requestData = {
      method: "post",
      headers: {
        "Content-Type": "application/json; multipart/form-data; charset=utf-8",
      },
      data: {
        catalog_name: voucherName,
        points_needed: pointsNeeded,
        image: voucherImage,
        image_detail: voucherImageDetail,
        expiry_date: expiryDate,
        catalog_type: voucherType,
        is_voucher: 1,
        catalog_product: merchant,
        point_type_used: pointTypeUsed,
        tenant_id: tenantId,
        category_id: categoryId,
        is_active: isActive,
        max_claim: maxClaim,
        _method: "PUT",
      },
      url: "admin/catalogs/" + voucherId,
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      const data = res.data.data;

      return data;
    } catch (error) {
      throw error;
    }
  },

  deleteVoucher: async (voucherId: string) => {
    const requestData = {
      method: "delete",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      url: "admin/catalogs/" + voucherId,
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      const data = res.data.data;

      return data;
    } catch (error) {
      throw error;
    }
  },

  updateVoucherDescription: async (payload: CatalogDescriptionPayloadTypes) => {
    const {
      catalogId,
      title,
      termsAndCondition,
      purcaseAndUse,
      titleEn,
      termsAndConditionEn,
      purcaseAndUseEn,
    } = payload;

    const requestData = {
      method: "post",
      headers: {
        "Content-Type": "application/json; multipart/form-data; charset=utf-8",
      },
      data: {
        catalog_id: catalogId,
        title: title,
        terms_and_condition: termsAndCondition,
        purcase_and_use: purcaseAndUse,
        title_en: titleEn,
        terms_and_condition_en: termsAndConditionEn,
        purcase_and_use_en: purcaseAndUseEn,
        _method: "PUT",
      },
      url: "admin/catalogs-description",
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      const data = res.data.data;

      return data;
    } catch (error) {
      throw error;
    }
  },

  releaseVoucher: async (payload: ReleaseVoucherPayloadTypes) => {
    const { phoneNumber, voucherCode } = payload;

    const requestData = {
      method: "post",
      headers: {
        "Content-Type": "application/json; multipart/form-data; charset=utf-8",
      },
      data: {
        phone_number: phoneNumber,
        voucher_code: voucherCode,
      },
      url: "admin/catalogs/release-voucher",
    };

    try {
      const res = await ApiService.apiRequest(requestData);
      const data = res.data.data;

      return data;
    } catch (error) {
      throw error;
    }
  },
};

export default VoucherService;
